import { useEffect, useRef, useState } from "react";
import {
  statusCodeIsPinned,
  StatusCodeFilter,
} from "../statusCodeFilter";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { TMapRef } from "../type";
import { DEFAULT_REPLAY_SPEED } from "./ReplayControlsPanel";
import { replayTrips } from "./replayTrips";
import { createTooltipController } from "./tooltip";
import { DateGroup } from "./useMultiDateTripData";
import { tripDisplayNumberOr, tripDisplayNumbers } from "./tripNumbering";

export const REPLAY_SPEED_OPTIONS = [1, 2, 5, 10];

// how long the finished replay stays up before the routes come back
const END_HOLD_MS = 1500;

// replays a single trip's events one at a time - this is specifically the
// "go into a trip's details, click Replay events" flow (event-by-event
// markers). "Replay All" is a different animation (the day-by-day
// chronological line-draw, replayed on demand) - see
// useMultiDayMapRender's `replay()`.
export const useMultiDayReplay = ({
  mapRef,
  dateGroups,
  speedUnit,
  filteredStatusCodes,
}: {
  mapRef: React.MutableRefObject<TMapRef>;
  dateGroups: DateGroup[];
  // the account's configured speed unit, for the per-event hover tooltip
  speedUnit?: SPEED_UNITS;
  // the Event Filter's current selection - the replay pins only the events
  // it is showing, so it can't put filtered-out pins back on the map
  filteredStatusCodes?: StatusCodeFilter;
}) => {
  const [replayingTripKey, setReplayingTripKey] = useState<string | null>(
    null
  );
  const [replayPercentage, setReplayPercentage] = useState<number | null>(
    null
  );
  // the reported time of the event just revealed - the universal clock for
  // this replay. exact, since it walks real reported fixes one at a time.
  const [replayClock, setReplayClock] = useState<number | null>(null);
  // the asset's reported speed at the fix on the clock - the timestamp on
  // its own invites the question "and how fast were they going?"
  const [replaySpeedKph, setReplaySpeedKph] = useState<number | null>(null);
  const [replaySpeed, setReplaySpeed] = useState<number>(DEFAULT_REPLAY_SPEED);

  const filteredStatusCodesRef = useRef(filteredStatusCodes);
  filteredStatusCodesRef.current = filteredStatusCodes;

  const speedRef = useRef(replaySpeed);
  useEffect(() => {
    speedRef.current = replaySpeed;
  }, [replaySpeed]);

  // the ref too, not just the state: a replay starting in this same tick
  // reads the ref before the effect above has had a chance to sync it
  const resetSpeed = () => {
    speedRef.current = DEFAULT_REPLAY_SPEED;
    setReplaySpeed(DEFAULT_REPLAY_SPEED);
  };

  const handleRef = useRef<{
    stop: () => void;
    pause: () => void;
    resume: () => void;
  } | null>(null);
  const tooltipRef = useRef<ReturnType<typeof createTooltipController> | null>(
    null
  );
  const [isPaused, setIsPaused] = useState(false);

  const stopReplay = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    tooltipRef.current?.destroy();
    tooltipRef.current = null;
    setReplayingTripKey(null);
    setReplayPercentage(null);
    setReplayClock(null);
    setReplaySpeedKph(null);
    setIsPaused(false);
    resetSpeed();
  };

  const togglePause = () => {
    if (!handleRef.current) return;
    if (isPaused) handleRef.current.resume();
    else handleRef.current.pause();
    setIsPaused((paused) => !paused);
  };

  const startReplay = (tripKey: string) => {
    const map = mapRef.current?.map;
    if (!map) return;

    const separatorIndex = tripKey.indexOf("__");
    const dateKey = tripKey.slice(0, separatorIndex);
    const tripIndex = parseInt(tripKey.slice(separatorIndex + 2), 10);
    const group = dateGroups.find((candidate) => candidate.dateKey === dateKey);
    const points = group?.trips[tripIndex];
    if (!group || !points || points.length === 0) return;

    handleRef.current?.stop();
    tooltipRef.current?.destroy();

    setReplayingTripKey(tripKey);
    setReplayPercentage(0);
    setReplayClock(points[0]?.timestamp ?? null);
    setIsPaused(false);
    resetSpeed();

    const tooltip = createTooltipController();
    tooltipRef.current = tooltip;

    const handle = replayTrips({
      map,
      trips: [
        {
          points,
          color: group.color,
          label: `${group.label} · Trip ${tripDisplayNumberOr(
            tripDisplayNumbers(group.trips),
            tripIndex
          )}`,
        },
      ],
      tooltip,
      isPinned: (point) =>
        statusCodeIsPinned(filteredStatusCodesRef.current, point.statuscode),
      getSpeedMultiplier: () => speedRef.current,
      speedUnit,
      onProgress: (percent, clock, speedKph) => {
        setReplayPercentage(percent);
        if (clock !== undefined) setReplayClock(clock);
        setReplaySpeedKph(speedKph ?? null);
      },
      // finishing used to leave the replay "running" at 100% - the routes
      // stayed down and Replay Events stayed a full progress bar until the
      // panel's close button was found. It ends itself now, after a moment
      // on the finished picture.
      onDone: () => {
        setTimeout(() => {
          if (handleRef.current === handle) stopReplay();
        }, END_HOLD_MS);
      },
    });
    handleRef.current = handle;
  };

  // stop any active replay on unmount (e.g. leaving multi-day mode)
  useEffect(() => stopReplay, []);

  return {
    replayingTripKey,
    replayPercentage,
    replayClock,
    replaySpeedKph,
    replaySpeed,
    setReplaySpeed,
    startReplay,
    stopReplay,
    togglePause,
    isPaused,
    isReplaying: replayingTripKey !== null,
  };
};
